import { useQuery, type UseQueryResult } from '@tanstack/react-query';

import { backendOrpc } from '@/infrastructures/orpcBackend/client';
import type { BackendRouterOutputs } from '@/infrastructures/orpcBackend/router';
import type { ChatScopeInputValue } from '@/infrastructures/orpcBackend/routers/chats.schema';

const MIN_REFETCH_INTERVAL_MS = 1000;
const MAX_REFETCH_INTERVAL_MS = 24 * 60 * 60 * 1000;

export function useChatAvailabilityQuery(scope: ChatScopeInputValue | undefined): {
  hasScope: boolean;
  query: UseQueryResult<BackendRouterOutputs['chats']['getChatAvailability']>;
} {
  const availabilityInput = scope?.materialId
    ? { materialId: scope.materialId }
    : scope?.courseId
      ? { courseId: scope.courseId }
      : undefined;
  const query = useQuery(
    backendOrpc.chats.getChatAvailability.queryOptions({
      input: availabilityInput ?? { materialId: '' },
      enabled: !!availabilityInput,
      // Refetch on every navigation into a scope and on focus, so a setting changed by someone else (e.g., examination
      // mode) applies when the page is displayed again. Cached data keeps rendering meanwhile, and the server memoizes
      // the result.
      staleTime: 0,
      // Refetch when a not-yet-opened material opens so the chat becomes available without a reload. The duration is
      // measured from when the response arrived (both timestamps are client time), so a remount that reuses cached data
      // does not push the refetch past the opening. The interval is capped because timers overflow past about 24.8
      // days and would then fire immediately in a loop.
      refetchInterval: (query) => {
        const msUntilChatOpens = query.state.data?.msUntilChatOpens;
        // After a failed fetch the previous data (with an expired duration) remains, so stop polling and leave recovery
        // to the retries and the refetch on focus or remount.
        if (msUntilChatOpens === undefined || query.state.status === 'error') return false;
        const remainingMs = msUntilChatOpens - (Date.now() - query.state.dataUpdatedAt);
        return Math.min(Math.max(remainingMs, MIN_REFETCH_INTERVAL_MS), MAX_REFETCH_INTERVAL_MS);
      },
    })
  );
  return { hasScope: !!availabilityInput, query };
}
